import React, { useState } from 'react';
import { Search, Database, Flame, Droplet, Zap, Download } from 'lucide-react';
import Icon from '../../../components/AppIcon';


const HazardLibrary = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedHazards, setSelectedHazards] = useState([]);

  const hazardLibrary = [
    {
      id: 'HAZ-001',
      name: 'Flammable Liquid Fire',
      category: 'flammability',
      description: 'Fire risk from flammable liquid ignition',
      typicalCauses: ['Static discharge', 'Hot surfaces', 'Electrical sparks', 'Open flames'],
      consequences: ['Fire', 'Explosion', 'Burns', 'Property damage'],
      safeguards: ['Proper bonding and grounding', 'Explosion-proof equipment', 'Fire suppression systems'],
      industryStandard: 'NFPA 30'
    },
    {
      id: 'HAZ-002',
      name: 'Toxic Gas Release',
      category: 'toxicity',
      description: 'Acute exposure to toxic gases or vapors',
      typicalCauses: ['Equipment failure', 'Process upsets', 'Loss of containment', 'Maintenance activities'],
      consequences: ['Respiratory injury', 'Poisoning', 'Fatality', 'Environmental impact'],
      safeguards: ['Gas detection systems', 'Emergency isolation', 'Respiratory protection', 'Ventilation'],
      industryStandard: 'OSHA 29 CFR 1910.1000'
    },
    {
      id: 'HAZ-003',
      name: 'Electrical Arc Flash',
      category: 'energySources',
      description: 'Arc flash from electrical equipment',
      typicalCauses: ['Equipment failure', 'Human error', 'Inadequate maintenance', 'Foreign objects'],
      consequences: ['Severe burns', 'Electrocution', 'Blast injury', 'Fire'],
      safeguards: ['Arc flash labeling', 'PPE requirements', 'Lockout/tagout', 'Training'],
      industryStandard: 'NFPA 70E'
    },
    // ... more hazards would be here
  ];

  const categories = [
    { id: 'all', label: 'All Categories', icon: Database },
    { id: 'flammability', label: 'Flammability', icon: Flame },
    { id: 'toxicity', label: 'Toxicity', icon: Droplet },
    { id: 'energySources', label: 'Energy Sources', icon: Zap }
  ];

  const filteredHazards = hazardLibrary?.filter(hazard => {
    const matchesSearch =
      hazard?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      hazard?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesCategory =
      selectedCategory === 'all' || hazard?.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const toggleHazardSelection = (hazardId) => {
    setSelectedHazards(prev =>
      prev?.includes(hazardId)
        ? prev?.filter(id => id !== hazardId)
        : [...prev, hazardId]
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Hazard Library</h2>
            <p className="text-sm text-gray-600 mt-1">
              Preloaded hazard databases categorized by industry standards
            </p>
          </div>
          <button
            disabled={selectedHazards?.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4" />
            Add Selected ({selectedHazards?.length})
          </button>
        </div>
      </div>
      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search hazards..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="flex gap-2">
            {categories?.map((cat) => {
              const Icon = cat?.icon;
              return (
                <button
                  key={cat?.id}
                  onClick={() => setSelectedCategory(cat?.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                    selectedCategory === cat?.id
                      ? 'bg-blue-50 border-blue-500 text-blue-700' :'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {cat?.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      {/* Hazards Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {filteredHazards?.map((hazard) => (
          <div
            key={hazard?.id}
            className={`bg-white rounded-lg shadow-sm border-2 p-6 cursor-pointer transition-all ${
              selectedHazards?.includes(hazard?.id)
                ? 'border-blue-500 bg-blue-50' :'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => toggleHazardSelection(hazard?.id)}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-semibold text-gray-900">{hazard?.name}</h3>
                <span className="text-xs text-gray-600 mt-1">{hazard?.id}</span>
              </div>
              <input
                type="checkbox"
                checked={selectedHazards?.includes(hazard?.id)}
                onChange={() => toggleHazardSelection(hazard?.id)}
                className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <p className="text-sm text-gray-700 mb-4">{hazard?.description}</p>

            <div className="space-y-3">
              <div>
                <h4 className="text-xs font-semibold text-gray-700 mb-1">Typical Causes:</h4>
                <div className="flex flex-wrap gap-1">
                  {hazard?.typicalCauses?.map((cause, idx) => (
                    <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                      {cause}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="text-xs font-semibold text-gray-700 mb-1">Safeguards:</h4>
                <ul className="list-disc list-inside text-xs text-gray-600 space-y-1">
                  {hazard?.safeguards?.slice(0, 2)?.map((sg, idx) => (
                    <li key={idx}>{sg}</li>
                  ))}
                </ul>
              </div>

              <div className="pt-2 border-t border-gray-200">
                <span className="text-xs text-gray-600">Standard: </span>
                <span className="text-xs font-medium text-gray-900">{hazard?.industryStandard}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default HazardLibrary;